import uniqueId from 'lodash/uniqueId';
import React, { useState, useRef } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import Overlay from 'react-bootstrap/Overlay';
import type { OverlayInjectedProps } from 'react-bootstrap/Overlay';
import {
	BsPencilFill,
	BsTrash3,
	BsEye,
	BsEyeSlash,
	BsArrowsMove,
	BsFileEarmarkPost,
	BsImageFill,
} from 'react-icons/bs';
import { FiCopy } from 'react-icons/fi';

import BayTooltip from '../../BayTooltip';
import MenuIcon from '../../Icons/MenuIcon';

interface RowData {
	_type: string;
	isHidden?: boolean;
	[key: string]: unknown;
}

interface EditCellProps {
	rowData: RowData;
	onChangeFunc: (action: string, event: React.MouseEvent, rowData: RowData) => void;
	isAllowedToPreview?: boolean;
	disableEdit?: boolean;
	isVideoFile?: boolean;
}

interface EditOption {
	value: string;
	label: string;
	icon: React.ReactNode;
	disabled?: boolean;
}

const EditCell: React.FC<EditCellProps> = ({
	rowData,
	onChangeFunc,
	isAllowedToPreview = true,
	disableEdit = false,
	isVideoFile = false,
}) => {
	const [show, setShow] = useState(false);
	const target = useRef<HTMLDivElement>(null);

	const editOptions: EditOption[] = [
		{
			value: 'preview',
			label: 'Preview',
			icon: <BsFileEarmarkPost className="me-2" />,
			disabled: !isAllowedToPreview,
		},
		{
			value: 'visibility',
			label: rowData?.isHidden ? 'Show' : 'Hide',
			icon: rowData?.isHidden ? (
				<BsEye className="me-2" />
			) : (
				<BsEyeSlash className="me-2" />
			),
		},
	];

	// Homepage option removed from files - only folders can be presented on the Homepage
	editOptions.push(
		{ value: 'copy', label: 'Copy To', icon: <FiCopy className="me-2" /> },
		{
			value: 'move',
			label: 'Move To',
			icon: <BsArrowsMove className="me-2" />,
		},
		{
			value: 'rename',
			label: 'Rename',
			icon: <BsPencilFill className="me-2" />,
		},
	);

	if (isVideoFile) {
		editOptions.push({
			value: 'editThumbnail',
			label: 'Edit Thumbnail',
			icon: <BsImageFill className="me-2" />,
		});
	}

	editOptions.push({
		value: 'delete',
		label: 'Delete',
		icon: <BsTrash3 className="me-2" />,
	});

	const editFolderOptions: EditOption[] = [
		{
			value: 'move',
			label: 'Move To',
			icon: <BsArrowsMove className="me-2" />,
		},
		{
			value: 'edit',
			label: 'Edit',
			icon: <BsPencilFill className="me-2" />,
		},
		{
			value: 'delete',
			label: 'Delete',
			icon: <BsTrash3 className="me-2" />,
		},
	];

	const handleActionClicked = (eventKey: string, event: React.MouseEvent, rowData: RowData) => {
		event.stopPropagation();
		event.preventDefault();
		onChangeFunc(eventKey, event, rowData);
		setShow(false);
	};

	const rowOptions = (): EditOption[] => {
		if (rowData._type === 'Entry$Folder') {
			return [...editFolderOptions].filter((data) => data !== null);
		} else {
			return [...editOptions].filter((data) => data !== null);
		}
	};

	const renderDropDownItem = (option: EditOption, index: number) => {
		return (
			<Dropdown.Item
				key={index}
				as="span"
				className="cursor-pointer my-2"
				disabled={option?.disabled}
				eventKey="edit"
				onClick={(event) => {
					handleActionClicked(option.value, event as unknown as React.MouseEvent, rowData);
				}}
			>
				{option.icon}
				{option.label}
			</Dropdown.Item>
		);
	};

	return (
		<div>
			<div
				className={`${
					rowData._type === 'Entry$Folder' && disableEdit
						? 'disable-action'
						: ''
				} px-2 d-flex align-items-center`}
				onClick={() => {
					setShow(!show);
				}}
			>
				<div
					ref={target}
					className={`col-action-button text-primary cursor-pointer ${show ? 'col-button' : ''}`}
				>
					<MenuIcon />
				</div>
			</div>
			<Overlay
				container={document.body}
				placement="bottom-end"
				popperConfig={{
					modifiers: [{ name: 'offset', options: { offset:[-130, 0] } }],
				}}
				rootClose
				show={show}
				target={target.current}
				onHide={() => setShow(false)}
			>
				{(props) => {
					// NOTE: 'hasDoneInitialMeasure' is a prop that is used
					// only for the Tooltip component
					const overlayProps = props as unknown as {
						ref: React.Ref<HTMLDivElement>;
						style?: React.CSSProperties;
						'aria-labelledby'?: string;
					};
					return (
						<div
							ref={overlayProps.ref}
							aria-labelledby={overlayProps['aria-labelledby']}
							style={{ zIndex: 10, ...overlayProps.style }}
						>
							<Dropdown
								autoClose
								align="end"
								className="d-flex filename-action-menu"
								id={uniqueId('row_dropdown_')}
								show
							>
								<Dropdown.Menu>
									{rowOptions().map((option, index) => {
										return option?.disabled ? (
											<BayTooltip
												key={index}
												overlay={
													option?.disabled
														? 'Preview not available for this file type.'
														: ''
												}
												placement="top"
											>
												<span>{renderDropDownItem(option, index)}</span>
											</BayTooltip>
										) : (
											renderDropDownItem(option, index)
										);
									})}
								</Dropdown.Menu>
							</Dropdown>
						</div>
					);
				}}
			</Overlay>
		</div>
	);
};

export default EditCell;
